import React from "react";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "@mui/material";

export enum Percentile {
  P_99_9 = "0.999",
  P_99 = "0.99",
  P_90 = "0.9",
  P_95 = "0.95",
  P_80 = "0.8",
}

export const Str2Percentile = (str: string) =>
  /*
We explicitly return the percentile in the case of "99.9" as the division  99.9/100 gives back a weird result of the type
0.99900000001 which then leads to return undefined when maps to enumerator.
*/
  str === "99.9"
    ? Percentile.P_99_9
    : Object.values(Percentile).find((option) => option === (Number(str) / 100).toString());

interface PercentileOption {
  label: string;
  value: Percentile;
}

const percentileOptions: PercentileOption[] = [
  { label: "99.9%", value: Percentile.P_99_9 },
  { label: "99%", value: Percentile.P_99 },
  { label: "95%", value: Percentile.P_95 },
  { label: "90%", value: Percentile.P_90 },
  { label: "80%", value: Percentile.P_80 },
];

interface PercentileSelectorProps {
  value?: Percentile;
  onChange?: (value: Percentile) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  availableOptions?: Percentile[];
}

const PercentileSelector = ({
  value = Percentile.P_99,
  disabled,
  sx = undefined,
  onChange = (_) => {},
  availableOptions = undefined,
}: PercentileSelectorProps) => {
  React.useEffect(() => {
    onChange(value);
  }, [value, onChange]);

  const handleChange = (event: SelectChangeEvent<Percentile>) => {
    const selectedValue = event.target.value as Percentile;
    onChange(selectedValue);
  };

  const label = "Percentile";

  const filteredOptions = availableOptions?.length
    ? percentileOptions.filter((option) => availableOptions.includes(option.value))
    : percentileOptions;

  return (
    <FormControl sx={{ width: 120, ...sx }} disabled={disabled}>
      <InputLabel id="percentile-selector-label">{label}</InputLabel>
      <Select labelId="percentile-selector-label" size="small" label={label} value={value} onChange={handleChange}>
        {filteredOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default PercentileSelector;
